/**
 * マスタ検索結果のデータテーブル表示コンポーネント
 * 検索結果のリスト表示、行選択、スクロール制御を担当します。
 */
interface MasterSearchDataTableProps {
  data: any[];                          // 表示対象のデータ配列
  columns: any[];                       // 動的に生成されたカラム定義
  isLoading: boolean;                   // データ取得中のフラグ
  selectedRow: any;                     // 現在選択されている行データ
  onRowClick: (row: any) => void;       // 行クリック時のハンドラ（選択状態の更新）
  onRowDoubleClick: (row: any) => void; // 行ダブルクリック時のハンドラ（確定処理）
}

export const MasterSearchDataTable = ({
  data,
  columns,
  isLoading,
  selectedRow,
  onRowClick,
  onRowDoubleClick,
}: MasterSearchDataTableProps) => {
  // --- 高さ制御用の定数設定 ---
  const TABLE_MIN_H_5_ROWS = '350px'; // 5行分程度の最低高さ
  const EXTERNAL_H = '300px';         // 検索フォームやヘッダーなど、テーブル以外の外枠の高さ概算
  
  // データが一定数以上ある場合にスクロールを有効化するための判定
  const hasMinRows = data.length >= 5;

  return (
    <div className="flex-none p-5 bg-white">
      {/* テーブルの外枠コンテナ：
        minHeight: データがある程度あるときは高さを確保し、スカスカになるのを防ぐ
        maxHeight: 画面の高さ（90vh）から外枠分を引いた値と最低高さを比較し、
                   画面内に収まる最適な高さを計算してスクロールを発生させる
      */}
      <div 
        className="flex flex-col border border-slate-200 rounded overflow-hidden"
        style={{ 
          minHeight: hasMinRows ? TABLE_MIN_H_5_ROWS : 'auto',
          maxHeight: hasMinRows ? `max(${TABLE_MIN_H_5_ROWS}, calc(90vh - ${EXTERNAL_H}))` : 'none'
        }}
      >
        {/* スクロール可能なエリア */}
        <div className="overflow-auto no-scrollbar flex-1">
          <table className="w-full text-left border-collapse min-w-full">
            {/* テーブルヘッダー：
              sticky top-0 により、スクロールしてもヘッダーが上部に固定される
            */}
            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
              <tr>
                {columns.map((col, i) => (
                  <th 
                    key={i} 
                    className="px-4 py-2.5 text-sm font-bold text-slate-500 border-b border-slate-200 bg-slate-50 whitespace-nowrap"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            
            <tbody className="divide-y divide-slate-100 bg-white">
              {isLoading ? (
                // ローディング中
                <tr>
                  <td colSpan={columns.length} className="text-center py-20 text-slate-400 text-sm">
                    読込中...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                // データあり：行レンダリング
                data.map((row, rowIndex) => (
                  <tr 
                    key={rowIndex} 
                    onClick={() => onRowClick(row)} 
                    onDoubleClick={() => onRowDoubleClick(row)}
                    className={`cursor-pointer transition-colors ${
                      selectedRow === row 
                        ? 'bg-indigo-50 text-indigo-700 font-medium' // 選択中のスタイル
                        : 'hover:bg-slate-50 text-slate-600'         // 未選択・ホバー時のスタイル
                    }`}
                  >
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="px-4 py-2.5 text-sm truncate">
                        {row[col.accessor]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                // 検索結果が0件の場合
                <tr>
                  <td colSpan={columns.length} className="text-center py-20 text-slate-400 text-sm">
                    該当なし
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};